import React from 'react';
import PropTypes from 'prop-types';
import { Card, Table, Tabs, Tab } from 'react-bootstrap';
import { rad2deg } from './DesignTool/utils';
import { getPersistedPanelCounts } from './summary-panel-counts';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function ShadeReport({ wattage, segments, panels, optPitchAndAzimuth, stringingData, productionPerPanelData }) {
  const validPanels = Array.isArray(panels) ? panels.filter(Boolean) : [];
  const persistedPanelCounts = getPersistedPanelCounts(stringingData, productionPerPanelData);
  const annualData = segments.map(
    ({ id, acAnnual, acMonthly, solarAccess, tsrf, tof, annual_kwh, selected_panel_count }, segmentIndex) => {
      const panel = validPanels.find((pnl) => pnl.id === id);
      // if (panel === undefined) return null;

      const annualTSRF = tsrf?.[12] || 0;
      const annualTOF = tof?.[12] || 0;
      const annualSolarAccess = solarAccess?.[12] || 0;

      // Get max panel count for this segment (all panels placed)
      const maxPanels = panel ? panel.horizontal.points.length + panel.vertical.points.length : 0;

      // Use annual_kwh from solar-production API if available
      // Check for undefined/null explicitly - 0 is a valid value (no panels selected on this roof)
      let total, each, displayPanelCount;
      if (annual_kwh !== undefined && annual_kwh !== null) {
        // Use actual production from solar-production API
        // annual_kwh can be 0 if no panels are selected on this roof (e.g., target production mode)
        total = annual_kwh.toFixed(2);
        // Use selected_panel_count if available (from target production mode)
        // If annual_kwh is 0 and selected_panel_count is not set, assume 0 panels selected
        const effectivePanelCount =
          selected_panel_count !== undefined && selected_panel_count !== null
            ? selected_panel_count
            : persistedPanelCounts
              ? (persistedPanelCounts.get(segmentIndex) ?? 0)
              : annual_kwh === 0
                ? 0
                : maxPanels;
        displayPanelCount = effectivePanelCount;
        each = effectivePanelCount > 0 ? annual_kwh / effectivePanelCount : 0;
      } else {
        // Fall back to old calculation method (shading-based estimation)
        // Only used when production has NOT been calculated via solar-production API
        acAnnual = acMonthly?.reduce ? acMonthly.reduce((a, b) => a + b, 0) : 0;
        each = (acAnnual * annualSolarAccess * wattage) / 1000;
        total = panel && maxPanels > 0 ? (each * maxPanels).toFixed(2) : '--';
        displayPanelCount = maxPanels;
      }

      return {
        id,
        number: displayPanelCount || '--',
        each,
        total,
        annualSolarAccess,
        annualTSRF,
        annualTOF,
      };
    }
  ); // .filter((item) => Boolean(item));

  return (
    <Card className="mx-auto mt-5 shadow" body>
      <h1 className="mb-3 text-center font-weight-light">Design Summary</h1>
      <Table striped borderless hover size="sm">
        <thead>
          <tr>
            <th>#</th>
            <th>Panels</th>
            <th>Annual Production/Panel</th>
            <th>Total Annual Production</th>
            <th>Solar Access</th>
            <th>TSRF</th>
            <th>TOF</th>
          </tr>
        </thead>
        <tbody>
          {annualData.map(({ id, number, each, total, annualSolarAccess, annualTSRF, annualTOF }, index) => (
            <tr key={id}>
              <td>{index + 1}</td>
              <td>{number}</td>
              <td>{`${each?.toFixed(2)} kWh`}</td>
              <td>{`${total} kWh`}</td>
              <td>{annualSolarAccess?.toFixed(3)}</td>
              <td>{annualTSRF?.toFixed(3)}</td>
              <td>{annualTOF?.toFixed(3)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
      {optPitchAndAzimuth && (
        <p>{`The TOF and TSRF values are based on optimal pitch ${optPitchAndAzimuth.pitch?.toFixed(1)}, and azimuth ${optPitchAndAzimuth.azimuth?.toFixed(1)}.`}</p>
      )}
      <Tabs defaultActiveKey="access" id="uncontrolled-tab-example" className="mb-3">
        <Tab eventKey="access" title="Solar Access">
          <h5 className="mt-4">Monthly Solar Access Per Roof Segment</h5>
          <Table striped borderless hover size="sm" title="Solar Access">
            <thead>
              <tr>
                <th>#</th>
                {MONTHS.map((month) => (
                  <th key={month}>{month}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {segments.map((seg, index) => (
                <tr key={seg.id}>
                  <td>{index + 1}</td>
                  {seg.solarAccess.map((value, idx) =>
                    idx < 12 ? <td key={`${seg.id}-${idx + 1}`}>{value?.toFixed(3)}</td> : null
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        </Tab>
        <Tab eventKey="tsrf" title="TSRF">
          <h5 className="mt-4">Monthly TSRF Per Roof Segment</h5>
          <Table striped borderless hover size="sm" title="TSRF">
            <thead>
              <tr>
                <th>#</th>
                {MONTHS.map((month) => (
                  <th key={month}>{month}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {segments.map((seg, index) => (
                <tr key={seg.id}>
                  <td>{index + 1}</td>
                  {seg.tsrf.map((value, idx) =>
                    idx < 12 ? <td key={`${seg.id}-${idx + 1}`}>{value?.toFixed(3)}</td> : null
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        </Tab>
        <Tab eventKey="tof" title="TOF">
          <h5 className="mt-4">Monthly TOF Per Roof Segment</h5>
          <Table striped borderless hover size="sm" title="TOF">
            <thead>
              <tr>
                <th>#</th>
                {MONTHS.map((month) => (
                  <th key={month}>{month}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {segments.map((seg, index) => (
                <tr key={seg.id}>
                  <td>{index + 1}</td>
                  {seg.tof.map((value, idx) =>
                    idx < 12 ? <td key={`${seg.id}-${idx + 1}`}>{value?.toFixed(3)}</td> : null
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        </Tab>
      </Tabs>
    </Card>
  );
}

ShadeReport.propTypes = {
  wattage: PropTypes.number.isRequired,
  // segments
};

export default ShadeReport;
